

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSearchHistory, fetchReverseSearchHistory, setSelectedUser, fetchPremiumDetails } from '../../redux/search/SearchSlice';
import PaystackPop from '@paystack/inline-js';
import axios from 'axios';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { toast } from 'sonner';
// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const SearchDashboard = () => {
  const dispatch = useDispatch();
  const { searchHistory, reverseSearchHistory, selectedUser, premiumDetails, loading, error } = useSelector((state) => state.search);
  const { currentUser } = useSelector((state) => state.user);
  const waveCanvasRef = useRef(null);
const [payingFor, setPayingFor] = useState(null);
  useEffect(() => {
    if (currentUser?.uniqueNumber) {
      console.log('Fetching search history for user:', currentUser.uniqueNumber);
      dispatch(fetchSearchHistory());
      dispatch(fetchReverseSearchHistory());
    }
  }, [dispatch, currentUser]);

  useEffect(() => {
    console.log('Search History:', searchHistory);
    console.log('Reverse Search History:', reverseSearchHistory);
  }, [searchHistory, reverseSearchHistory]);

  // Water Wave Chart Animation
  useEffect(() => {
    const canvas = waveCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let phase = 0;

    const totalSearches = searchHistory.length + reverseSearchHistory.length;
    const amplitude = 20;
    const frequency = 0.02;
    const waveHeight = totalSearches > 0 ? Math.min(totalSearches * 5, 100) : 10;

    const drawWave = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(249, 115, 22, 0.2)'; // orange-500 with transparency
      ctx.beginPath();
      ctx.moveTo(0, canvas.height);

      for (let x = 0; x < canvas.width; x++) {
        const y = canvas.height / 2 + Math.sin(x * frequency + phase) * amplitude;
        ctx.lineTo(x, y - waveHeight);
      }

      ctx.lineTo(canvas.width, canvas.height);
      ctx.closePath();
      ctx.fill();

      // Gradient overlay
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(249, 115, 22, 0.5)');
      gradient.addColorStop(1, 'rgba(249, 115, 22, 0.1)');
      ctx.fillStyle = gradient;
      ctx.fill();

      phase += 0.05;
      animationFrameId = requestAnimationFrame(drawWave);
    };

    drawWave();
    return () => cancelAnimationFrame(animationFrameId);
  }, [searchHistory, reverseSearchHistory]);

  const handleViewDetails = (user) => {
    dispatch(setSelectedUser(user));
  };
const handlePayForPremium = async (user) => {
  console.log(user)
  if (!user) {
    toast.error("Invalid user selected");
    return;
  }

  const paystack = new PaystackPop();

  paystack.newTransaction({
    key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
    email: currentUser.email,
    amount: 300000, // ₦3,000 in kobo
    currency: "NGN",
    reference: `PREM_${currentUser.uniqueNumber}_${Date.now()}`, // Optional: better reference

    metadata: {
      userId: user,        // Important: Send uniqueNumber
      payerId: currentUser.uniqueNumber,
      purpose: "premium_access"
    },

    onSuccess: async (transaction) => {
      console.log("Payment successful, reference:", transaction.reference);

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}payment/verify`,
          {
            reference: transaction.reference,
            userId: user   // ← Must match backend lookup
          },
          {
            headers: { 
              Authorization: `Bearer ${localStorage.getItem('token')}` 
            },
          }
        );

        if (response.data.success) {
          toast.success("Payment successful! Premium access unlocked.");
          dispatch(fetchPremiumDetails(user));
        } else {
          toast.error(response.data.message || "Payment verification failed");
        }
      } catch (error) {
        console.error('Payment verification failed:', error.response?.data || error);
        toast.error(
          error.response?.data?.message || 
          "Payment was successful but verification failed. Contact support."
        );
      }
    },

    onCancel: () => {
      toast.info("Payment cancelled");
    },

    onClose: () => {
      console.log("Payment window closed");
    }
  });
};

  // Pie Chart Data
  const pieData = {
    labels: ['Searches You Made', 'Searches for You'],
    datasets: [
      {
        data: [searchHistory.length, reverseSearchHistory.length],
        backgroundColor: ['#f97316', '#1f2937'], // orange-500, gray-800
        borderColor: ['#ffffff', '#ffffff'],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#1f2937', font: { size: 14 } },
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6">
      <div className="container mx-auto">
        <h2 className="text-3xl font-bold text-center mb-8 text-orange-500 dark:text-orange-400">Search Dashboard</h2>

        {/* Error and Login Message */}
        {error && <p className="text-red-500 text-center mb-4">{error}</p>}
        {!currentUser && (
          <p className="text-red-500 text-center mb-4">Please log in to view your search history</p>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Pie Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-center">Search Distribution</h3>
            <Pie data={pieData} options={pieOptions} />
          </div>

          {/* Water Wave Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-center">Total Search Activity</h3>
            <canvas ref={waveCanvasRef} width={300} height={150} />
            <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-2">
              Total Searches: {searchHistory.length + reverseSearchHistory.length}
            </p>
          </div>
        </div>

        {/* Search History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Users You Searched For */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-orange-500 dark:text-orange-400">Users You Searched For</h3>
            {searchHistory.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No searches yet</p>
            ) : (
              <ul className="space-y-4">
                {searchHistory.map((entry) => (
                  entry?.user ? (
                    <li
                      key={entry.user.id}
                      className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                    >
                      <div>
                        <p className="font-medium">{entry.user.name} ({entry.user.userType})</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Searched at {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(entry.user)}
                          className="text-blue-500 hover:text-blue-600 underline"
                        >
                          View Details
                        </button>
                        {selectedUser?.id === entry.user.id && (
                          <button
                            onClick={() => handlePayForPremium(entry.user.id)}
                            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition"
                          >
                            Unlock Premium
                          </button>
                        )}
                      </div>
                    </li>
                  ) : null
                ))}
              </ul>
            )}
          </div>

          {/* Users Who Searched for You */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-orange-500 dark:text-orange-400">Users Who Searched for You</h3>
            {reverseSearchHistory.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No one has searched for you yet</p>
            ) : (
              <ul className="space-y-4">
                {reverseSearchHistory.map((entry) => (
                  entry?.user ? (
                    <li
                      key={entry.user.id}
                      className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                    >
                      <div>
                        <p className="font-medium">{entry.user.name} ({entry.user.userType})</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Searched at {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(entry.user)}
                          className="text-blue-500 hover:text-blue-600 underline"
                        >
                          View Details
                        </button>
                     <button
  onClick={() => handlePayForPremium(entry.user.id)}
  disabled={payingFor === entry.user.uniqueNumber}
  className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
>
  {payingFor === entry.user.uniqueNumber ? "Processing..." : "Unlock Premium"}
</button>
                      </div>
                    </li>
                  ) : null
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Selected User Details */}
        {selectedUser && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-orange-500 dark:text-orange-400">Basic Details</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Name:</span> {selectedUser.name || 'N/A'}</p>
              <p><span className="font-medium">User Type:</span> {selectedUser.userType || 'N/A'}</p>
            </div>
          </div>
        )}

        {/* Premium Details */}
        {premiumDetails && selectedUser?.id === premiumDetails.id && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-orange-500 dark:text-orange-400">Premium Details</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Email:</span> {premiumDetails.email || 'N/A'}</p>
              <p><span className="font-medium">Phone:</span> {premiumDetails.phoneNumber || 'N/A'}</p>
              {premiumDetails.userType === 'jobSeeker' && (
                <p><span className="font-medium">Job Mode:</span> {premiumDetails.jobMode || 'N/A'}</p>
              )}
              {premiumDetails.userType === 'jobEmployer' && (
                <p><span className="font-medium">Company Size:</span> {premiumDetails.size || 'N/A'}</p>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center mt-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchDashboard;